import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { Company } from "@/types";
import { CompanyForm, CompanyFormData } from "./CompanyForm"
;
import { UserRole } from "@/types";
import { UserRole as UserRoleEnum } from "@/types/enums";

interface EditCompanyDialogProps {
  company: Company | null;
  isOpen: boolean;
  onClose: () => void;
  onCompanyUpdated: (company: Company) => void;
}

export const EditCompanyDialog = ({
  company,
  isOpen,
  onClose,
  onCompanyUpdated
}: EditCompanyDialogProps) => {
  const [formData, setFormData] = useState<CompanyFormData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  useEffect(() => {
    if (company) {
      setFormData({
        name: company.name,
        contact_person: company.contact_person || "",
        contact_email: company.contact_email || "",
        contact_role: (company.contact_role || UserRoleEnum.APP_OWNER) as UserRole,
        domains: company.domains || [],
      });
    }
  }, [company]);
  
  const handleUpdateCompany = async (data: CompanyFormData) => {
    if (!company) return;
    
    try {
      setIsSubmitting(true);
      
      // Update the company in Supabase
      const { error } = await supabase
        .from('companies')
        .update({
          name: data.name,
          contact_person: data.contact_person || null,
          contact_email: data.contact_email,
          contact_role: data.contact_role as UserRole,
          domains: data.domains || []
        })
        .eq('id', company.id);
      
      if (error) {
        toast.error('Failed to update company');
        return;
      }
      
      // Create updated company object
      const updatedCompany: Company = {
        ...company,
        name: data.name,
        contact_person: data.contact_person || null,
        contact_email: data.contact_email,
        contact_role: data.contact_role as UserRole,
        domains: data.domains || []
      };
      
      onCompanyUpdated(updatedCompany);
      toast.success("Company updated successfully");
      onClose();
    } catch {
      toast.error('An error occurred while updating company');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!formData) {
    return null;
  }
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Company</DialogTitle>
          <DialogDescription>
            Update company information
          </DialogDescription>
        </DialogHeader>
        
        <CompanyForm
          initialData={formData}
          onSubmit={handleUpdateCompany}
          onCancel={onClose}
          submitLabel="Save Changes"
          isSubmitting={isSubmitting}
        />
      </DialogContent>
    </Dialog>
  );
};
